import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { deskApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import type { DeskKbArticle, DeskKbCategory } from '@/types/desk';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function DeskKnowledgeBasePage() {
  const [categories, setCategories] = useState<DeskKbCategory[]>([]);
  const [articles, setArticles] = useState<DeskKbArticle[]>([]);
  const [saving, setSaving] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [content, setContent] = useState('');

  const load = useCallback(() => {
    Promise.all([deskApi.listKbCategories(), deskApi.listKbArticles()])
      .then(([c, a]) => {
        setCategories(c);
        setArticles(a);
      })
      .catch((err) => showApiError(err, 'Failed to load knowledge base'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createCategory = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get('name') ?? '').trim();
    if (!name) return;
    setSaving(true);
    try {
      await deskApi.createKbCategory({ name });
      notify.success('Category created');
      e.currentTarget.reset();
      load();
    } catch (err) {
      showApiError(err, 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const createArticle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const title = String(fd.get('title') ?? '').trim();
    const categoryIdNum = categoryId ? Number(categoryId) : undefined;
    if (!title || !content.trim()) return;
    setSaving(true);
    try {
      await deskApi.createKbArticle({
        title,
        content: content.trim(),
        categoryId: categoryIdNum,
        published: fd.get('published') === 'on',
      });
      notify.success('Article created');
      e.currentTarget.reset();
      setCategoryId('');
      setContent('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Knowledge Base"
        description="Categories and articles for agents and customer portal"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Desk', to: '/desk' }, { label: 'Knowledge Base' }]}
      />

      <div className="grid gap-4 lg:grid-cols-2 mb-6">
        <form className="panel space-y-3" onSubmit={createCategory}>
          <h3 className="font-semibold">New category</h3>
          <Input name="name" label="Name" required />
          <Button type="submit" loading={saving}>
            Add category
          </Button>
        </form>

        <form className="panel space-y-3" onSubmit={createArticle}>
          <h3 className="font-semibold">New article</h3>
          <Input name="title" label="Title" required />
          <Select
            label="Category"
            placeholder="— None —"
            options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
            value={categoryId}
            onChange={setCategoryId}
          />
          <Textarea
            label="Content"
            name="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
          />
          <label className="flex items-center gap-2 text-sm">
            <input name="published" type="checkbox" />
            Publish immediately
          </label>
          <Button type="submit" loading={saving}>
            Add article
          </Button>
        </form>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel">
          <h3 className="font-semibold mb-2">Categories</h3>
          <ul className="text-sm space-y-1">
            {categories.map((c) => (
              <li key={c.id}>{c.name}</li>
            ))}
            {categories.length === 0 && <li className="text-slate-500">No categories</li>}
          </ul>
        </div>
        <div className="panel">
          <h3 className="font-semibold mb-2">Articles</h3>
          <ul className="text-sm space-y-2">
            {articles.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.title}</span>
                {a.published ? (
                  <span className="ml-2 text-xs text-green-600">Published</span>
                ) : (
                  <span className="ml-2 text-xs text-slate-400">Draft</span>
                )}
              </li>
            ))}
            {articles.length === 0 && <li className="text-slate-500">No articles yet</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
